/**
 * Context de Autenticação
 * Gerencia estado de autenticação global da aplicação
 */

import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import {
  setTokens,
  clearTokens,
  getAuthToken,
  getRefreshToken,
  apiServiceClient,
} from "../config/api";
import FEATURE_FLAGS from "../config/featureFlags";
import { roleHasBasePermission } from "../lib/roleBasePermissions";
import { isTokenExpiringSoon, isTokenExpired, getTokenTimeRemaining } from "../utils/jwt";

/**
 * Função helper para detectar tenant do subdomínio (sem usar hooks)
 */
const getTenantFromSubdomain = () => {
  if (typeof window === "undefined") return null;
  
  const hostname = window.location.hostname.split(":")[0];
  const parts = hostname.split(".");
  
  // Se for localhost com subdomínio (ex: educapaco.localhost)
  if (parts.length >= 2 && parts[parts.length - 1] === "localhost") {
    return parts[0];
  }
  
  // Para domínios reais (ex: educapaco.pacodolumiar.ma.gov.br)
  if (parts.length > 2) {
    return parts[0];
  }
  
  return null;
};

const AuthContext = createContext(null);

/**
 * Traduz erros de conexão para português brasileiro
 */
const translateError = error => {
  if (!error) return "Erro desconhecido";

  const errorMessage = error.message || error.toString() || "";

  // Erros de conexão
  if (
    errorMessage.includes("Failed to fetch") ||
    errorMessage.includes("NetworkError") ||
    errorMessage.includes("Network request failed") ||
    errorMessage.includes("fetch failed")
  ) {
    return "Erro de conexão: Não foi possível conectar ao servidor. Verifique sua conexão com a internet.";
  }

  if (errorMessage.includes("timeout") || errorMessage.includes("Timeout")) {
    return "Tempo de conexão esgotado. Tente novamente.";
  }

  if (errorMessage.includes("CORS") || errorMessage.includes("cors")) {
    return "Erro de configuração do servidor. Entre em contato com o suporte.";
  }

  // Erros HTTP comuns
  if (errorMessage.includes("401") || errorMessage.includes("Unauthorized")) {
    return "CPF ou senha incorretos";
  }

  if (errorMessage.includes("403") || errorMessage.includes("Forbidden")) {
    // Preservar mensagem de tenant (ex: "Usuário vinculado à Instituição X")
    if (errorMessage.includes("Instituição") || errorMessage.includes("vinculado")) {
      return errorMessage;
    }
    return "Acesso negado. Você não tem permissão para realizar esta ação.";
  }

  if (errorMessage.includes("404") || errorMessage.includes("Not Found")) {
    return "Serviço não encontrado. Verifique a configuração da API.";
  }

  if (errorMessage.includes("500") || errorMessage.includes("Internal Server Error")) {
    return "Erro interno do servidor. Tente novamente mais tarde.";
  }

  if (errorMessage.includes("503") || errorMessage.includes("Service Unavailable")) {
    return "Serviço temporariamente indisponível. Tente novamente mais tarde.";
  }

  // Retorna a mensagem original se já estiver em português ou se não for um erro conhecido
  return errorMessage;
};

/**
 * Provider de autenticação
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [effectivePermissions, setEffectivePermissions] = useState([]);
  const [backendGranularEnabled, setBackendGranularEnabled] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const refreshIntervalRef = useRef(null);
  const isRefreshingRef = useRef(false);

  /**
   * Busca dados do usuário atual via /auth/me
   */
  const fetchUserData = async token => {
    try {
      // Usa apiServiceClient que já adiciona o token automaticamente
      return await apiServiceClient.get("/auth/me");
    } catch (error) {
      const errorMessage = error.message || "Erro ao buscar dados do usuário";
      throw new Error(translateError(new Error(errorMessage)));
    }
  };

  /**
   * Busca permissões efetivas (modo granular).
   */
  const fetchEffectivePermissions = async () => {
    if (!FEATURE_FLAGS.granularPermissions) {
      setEffectivePermissions([]);
      setBackendGranularEnabled(false);
      setPermissionsLoaded(true);
      return [];
    }
    try {
      const result = await apiServiceClient.get("/auth/me/permissions");
      const permissions = Array.isArray(result?.effective_permissions) ? result.effective_permissions : [];
      setEffectivePermissions(permissions);
      setBackendGranularEnabled(Boolean(result?.granular_permissions_enabled ?? true));
      setPermissionsLoaded(true);
      return permissions;
    } catch (error) {
      console.warn("Erro ao buscar permissões efetivas, aplicando modo restritivo:", error);
      setEffectivePermissions([]);
      // Fail-safe: sem carregar permissões, mantém granular ativo para não conceder acesso por role.
      setBackendGranularEnabled(true);
      setPermissionsLoaded(true);
      return [];
    }
  };

  /**
   * Busca nome do tenant a partir do negocio_id
   */
  const getTenantNameFromNegocioId = async (negocioId, token) => {
    if (!negocioId) return null;
    
    try {
      // Buscar lista de tenants e encontrar o que corresponde ao negocio_id
      const tenants = await apiServiceClient.get("/auth/tenants");
      const tenant = Array.isArray(tenants) 
        ? tenants.find(t => t.negocio_id === negocioId)
        : null;
      return tenant?.nome || null;
    } catch (error) {
      console.warn("Erro ao buscar nome do tenant:", error);
      return null;
    }
  };

  /**
   * Verifica se o usuário pode acessar o tenant do subdomínio atual.
   * Retorna true se pode (TECNICO ou mesmo tenant), false caso contrário.
   */
  const userCanAccessCurrentTenant = async (userData) => {
    if (!userData || userData.role === "TECNICO") return true;
    const currentTenant = getTenantFromSubdomain();
    if (!currentTenant || !userData.negocio_id) return false;
    const userTenantName = await getTenantNameFromNegocioId(userData.negocio_id, getAuthToken());
    if (!userTenantName) return false;
    return userTenantName.toLowerCase().trim() === currentTenant.toLowerCase().trim();
  };

  // Verifica se há token salvo ao carregar a aplicação
  useEffect(() => {
    const initAuth = async () => {
      let token = getAuthToken();

      // Se não há token em memória, tentar recuperar usando o cookie HttpOnly
      if (!token) {
        try {
          const refreshResponse = await apiServiceClient.post("/auth/refresh", {});
          token = refreshResponse.access_token;
          if (token) {
            setTokens(token);
          }
        } catch (err) {
          // Sem cookie válido = não autenticado
          setLoading(false);
          return;
        }
      }

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Busca dados do usuário via /auth/me
        const userData = await fetchUserData(token);

        // Isolamento multitenant: se o usuário não for TECNICO, deve estar no subdomínio do seu tenant
        const canAccess = await userCanAccessCurrentTenant(userData);
        if (!canAccess) {
          clearTokens();
          setUser(null);
          setEffectivePermissions([]);
          setBackendGranularEnabled(false);
          setPermissionsLoaded(false);
          clearTokenRefreshInterval();
          setLoading(false);
          return;
        }

        await fetchEffectivePermissions();
        setUser(userData);
        setupTokenRefreshInterval();
      } catch (err) {
        // Token inválido, limpa storage
        console.warn("Token inválido ou expirado:", err);
        clearTokens();
        setUser(null);
        setEffectivePermissions([]);
        setBackendGranularEnabled(false);
        setPermissionsLoaded(false);
        clearTokenRefreshInterval();
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Cleanup ao desmontar
    return () => {
      clearTokenRefreshInterval();
    };
  }, []);

  // Listener para logout automático
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      setEffectivePermissions([]);
      setBackendGranularEnabled(false);
      setPermissionsLoaded(false);
      clearTokens();
      clearTokenRefreshInterval();
    };

    window.addEventListener("auth:logout", handleLogout);
    return () => {
      window.removeEventListener("auth:logout", handleLogout);
      clearTokenRefreshInterval();
    };
  }, []);

  /**
   * Realiza registro de novo usuário
   */
  const register = async (cpf, password, nome, role = "MONITOR", email = null) => {
    try {
      setLoading(true);
      setError(null);

      // Limpar CPF (remover formatação, deixar apenas números)
      const cpfClean = cpf.replace(/\D/g, "");

      const registerResponse = await apiServiceClient.post("/auth/register", {
        cpf: cpfClean,
        password,
        nome,
        role,
        ativo: true,
        email: email || null,
      });

      // Após registro bem-sucedido, faz login automaticamente com o CPF
      await login(cpfClean, password);

      return registerResponse;
    } catch (err) {
      let errorMessage = err.message || "Erro ao realizar cadastro";

      // Trata erro específico de CPF já cadastrado
      if (
        errorMessage.includes("CPF já cadastrado") ||
        errorMessage.includes("já cadastrado") ||
        errorMessage.toLowerCase().includes("already") ||
        errorMessage.toLowerCase().includes("exists")
      ) {
        errorMessage = "Este CPF já está cadastrado. Tente fazer login.";
      }

      const translatedMessage = translateError(new Error(errorMessage));
      setError(translatedMessage);
      throw new Error(translatedMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Realiza login
   */
  const login = async (cpf, password) => {
    try {
      setLoading(true);
      setError(null);

      // Obter tenant do subdomínio atual
      const tenantFromSubdomain = getTenantFromSubdomain();
      
      const cpfClean = cpf.replace(/\D/g, "");
      
      // Backend exige X-Tenant-Subdomain para validar isolamento multitenant
      if (!tenantFromSubdomain) {
        throw new Error(
          "Acesso por subdomínio inválido. Use o endereço correto da instituição (ex: educapaco.localhost ou ntme.localhost)."
        );
      }

      const loginResponse = await apiServiceClient.post(
        "/auth/login",
        { cpf: cpfClean, password },
        {
          headers: {
            "X-Tenant-Subdomain": tenantFromSubdomain,
          },
        }
      );

      // A API retorna { access_token, refresh_token, token_type }
      const accessToken = loginResponse.access_token;
      const refreshToken = loginResponse.refresh_token || accessToken;

      if (!accessToken) {
        throw new Error("Token não retornado pela API");
      }

      // Salva tokens
      setTokens(accessToken, refreshToken);

      // Busca dados completos do usuário via /auth/me
      let userData;
      try {
        userData = await fetchUserData(accessToken);
        
        // Validar se o tenant do usuário corresponde ao subdomínio
        // Apenas TECNICO pode acessar qualquer tenant
        if (userData.role !== "TECNICO" && tenantFromSubdomain && userData.negocio_id) {
          const userTenantName = await getTenantNameFromNegocioId(userData.negocio_id, accessToken);
          if (userTenantName && userTenantName.toLowerCase().trim() !== tenantFromSubdomain.toLowerCase().trim()) {
            clearTokens();
            setUser(null);
            setEffectivePermissions([]);
            setBackendGranularEnabled(false);
            setPermissionsLoaded(false);
            const instituicaoNome = userTenantName === "educapaco" ? "EducaPaço" : userTenantName === "ntme" ? "NTME" : userTenantName;
            throw new Error(`Usuário vinculado à Instituição ${instituicaoNome}. Acesse pelo endereço correto.`);
          }
        }
      } catch (meError) {
        // Se /auth/me falhar, tenta decodificar do JWT como fallback (mantendo validação de tenant)
        console.warn(
          "Não foi possível buscar dados via /auth/me, tentando decodificar JWT:",
          meError
        );
        try {
          const tokenParts = accessToken.split(".");
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            userData = {
              id: parseInt(payload.sub) || 0,
              email: payload.email || "",
              nome: "",
              role: payload.role || "MONITOR",
              negocio_id: payload.negocio_id || null,
            };
          } else {
            throw new Error("Token JWT inválido");
          }
        } catch (decodeError) {
          console.error("Erro ao decodificar JWT:", decodeError);
          userData = {
            id: 0,
            email: "",
            nome: "",
            role: "MONITOR",
            negocio_id: null,
          };
        }
      }

      // Validação de tenant também no fallback (evita bypass quando /auth/me falha)
      if (userData.role !== "TECNICO" && tenantFromSubdomain && userData.negocio_id) {
        const userTenantName = await getTenantNameFromNegocioId(userData.negocio_id, accessToken);
        if (userTenantName && userTenantName.toLowerCase().trim() !== tenantFromSubdomain.toLowerCase().trim()) {
          clearTokens();
          setUser(null);
          setEffectivePermissions([]);
          setBackendGranularEnabled(false);
          setPermissionsLoaded(false);
          const instituicaoNome = userTenantName === "educapaco" ? "EducaPaço" : userTenantName === "ntme" ? "NTME" : userTenantName;
          throw new Error(`Usuário vinculado à Instituição ${instituicaoNome}. Acesse pelo endereço correto.`);
        }
      }

      await fetchEffectivePermissions();
      setUser(userData);

      // Configura renovação automática após login bem-sucedido
      setupTokenRefreshInterval();

      return { token: accessToken, refreshToken, user: userData };
    } catch (err) {
      let errorMessage = err.message || "CPF ou senha incorretos";

      // Traduz mensagens de erro comuns
      if (
        errorMessage.toLowerCase().includes("incorrect") ||
        errorMessage.toLowerCase().includes("invalid") ||
        errorMessage.toLowerCase().includes("wrong")
      ) {
        errorMessage = "CPF ou senha incorretos";
      }

      const translatedMessage = translateError(new Error(errorMessage));
      setError(translatedMessage);
      throw new Error(translatedMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Realiza logout
   */
  const logout = async () => {
    try {
      setLoading(true);

      // Tenta fazer logout no servidor (opcional - pode não existir)
      try {
        await apiServiceClient.post("/auth/logout").catch(() => {
          // Ignora erro se endpoint não existir
        });
      } catch (err) {
        // Ignora erro se endpoint não existir
        console.warn("Erro ao fazer logout no servidor (ignorado):", err);
      }
    } catch (err) {
      console.error("Erro ao fazer logout:", err);
    } finally {
      setUser(null);
      setEffectivePermissions([]);
      setBackendGranularEnabled(false);
      setPermissionsLoaded(false);
      clearTokens();
      clearTokenRefreshInterval();
      setLoading(false);
    }
  };

  /**
   * Atualiza dados do usuário
   */
  const updateUser = userData => {
    setUser(userData);
  };

  const hasEffectivePermission = permissionCode => {
    if (!FEATURE_FLAGS.granularPermissions) return false;
    if (Array.isArray(effectivePermissions) && effectivePermissions.includes(permissionCode)) {
      return true;
    }
    if (backendGranularEnabled && permissionsLoaded) {
      return false;
    }
    return roleHasBasePermission(user?.role, permissionCode);
  };

  /**
   * Faz refresh do token
   */
  const refreshToken = async () => {
    // Evita múltiplos refreshes simultâneos
    if (isRefreshingRef.current) {
      // Aguarda um pouco e tenta novamente
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (isRefreshingRef.current) {
        throw new Error("Refresh em andamento, tente novamente.");
      }
    }

    const refreshTokenValue = getRefreshToken();
    if (!refreshTokenValue) {
      throw new Error("Token de renovação não encontrado. Faça login novamente.");
    }

    isRefreshingRef.current = true;
    try {
      const tokenResponse = await apiServiceClient.post("/auth/refresh", {
        refresh_token: refreshTokenValue,
      });

      const newAccessToken = tokenResponse.access_token;
      const newRefreshToken = tokenResponse.refresh_token || newAccessToken;

      setTokens(newAccessToken, newRefreshToken)
      return newAccessToken;
    } catch (err) {
      clearTokens();
      setUser(null);
      setEffectivePermissions([]);
      setBackendGranularEnabled(false);
      setPermissionsLoaded(false);
      window.dispatchEvent(new CustomEvent("auth:logout"));
      const translatedMessage = translateError(err);
      throw new Error(translatedMessage);
    } finally {
      isRefreshingRef.current = false;
    }
  };

  /**
   * Verifica e renova o token automaticamente se necessário
   */
  const checkAndRefreshToken = async () => {
    const token = getAuthToken();
    if (!token) {
      return; // Sem token, não há o que renovar
    }

    // Se já está fazendo refresh, não faz nada
    if (isRefreshingRef.current) {
      return;
    }

    // Verifica se o token está expirado
    if (isTokenExpired(token)) {
      console.warn("⚠️ Token expirado, tentando renovar...");
      try {
        await refreshToken();
      } catch (err) {
        console.error("Não foi possível renovar token expirado:", err);
        // O refreshToken já faz logout em caso de erro
      }
      return;
    }

    // Verifica se o token está próximo de expirar (5 minutos antes)
    if (isTokenExpiringSoon(token, 5)) {
      try {
        await refreshToken();
      } catch (err) {
        console.error("Não foi possível renovar token proativamente:", err);
        // Se falhar, não faz logout imediatamente - deixa o interceptor tratar no próximo 401
      }
    }
  };

  /**
   * Configura intervalo para verificação periódica do token
   */
  const setupTokenRefreshInterval = () => {
    // Limpa intervalo anterior se existir
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Verifica a cada 2 minutos
    refreshIntervalRef.current = setInterval(() => {
      checkAndRefreshToken();
    }, 2 * 60 * 1000); // 2 minutos

    // Também verifica imediatamente
    checkAndRefreshToken();
  };

  /**
   * Limpa o intervalo de verificação
   */
  const clearTokenRefreshInterval = () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    effectivePermissions,
    permissionsLoaded,
    granularPermissionsEnabled: FEATURE_FLAGS.granularPermissions && backendGranularEnabled,
    hasEffectivePermission,
    refreshEffectivePermissions: fetchEffectivePermissions,
    register,
    login,
    logout,
    updateUser,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook para usar o contexto de autenticação
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

export default AuthContext;
